import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../global/supabase';
import { CLIENT_ID, BRANDING, LOCATIONS, MEDICATIONS } from '../config';

// Default templates matching the seeded values
const DEFAULTS = {
  daily: {
    system_role: `You are an AI compliance analyst for Walton Fire Protection District's controlled substance inventory management system (NarcoticTrack). Your role is to analyze the last 24 hours of narcotic inventory logs across all units and generate a concise, actionable compliance report. You focus on identifying discrepancies, missing logs, policy violations, and patterns that require supervisor attention. You are direct, precise, and prioritize patient safety and DEA compliance.`,
    flag_criteria: `CRITICAL: Any unit with zero logs in 24h when active, medication quantity discrepancy without documented reason (such as Used on Patient/Restock or Expired Removal/Restock), missing/null required fields, negative quantities.
WARNING: Seal number changes without documentation, units with only 1 log in 24h, quantities reaching zero, same personnel logging all entries (no cross-verification).
INFO: Notable personnel notes, run numbers logged, routine seal verifications, new personnel.`,
    analysis_priorities: '',
    additional_instructions: '',
  },
  weekly: {
    system_role: `You are an AI compliance strategist for Walton Fire Protection District, generating a WEEKLY intelligence report for narcotic inventory and controlled substance management. Think like a Division Chief reviewing a week of operations. Focus on PATTERNS and TRENDS across the entire week, not individual daily incidents. Identify systemic issues, personnel patterns, compliance trajectory, and strategic recommendations. Your audience is senior leadership.`,
    flag_criteria: `CRITICAL: Any unit with zero logs in 24h when active, medication quantity discrepancy without documented reason (such as Used on Patient/Restock or Expired Removal/Restock), missing/null required fields, negative quantities.
WARNING: Seal number changes without documentation, units with only 1 log in 24h, quantities reaching zero, same personnel logging all entries (no cross-verification).
INFO: Notable personnel notes, run numbers logged, routine seal verifications, new personnel.`,
    analysis_priorities: `Compliance trends (improving/stable/declining)
Medication usage patterns (unusual consumption, zero-quantity events, restock anomalies)
Personnel accountability (coverage gaps, cross-verification)
Systemic issues (process/training gaps)
Daily flags follow-up (were critical flags addressed?)`,
    additional_instructions: '',
  },
};

function PromptTemplateEditor() {
  const [activeTab, setActiveTab] = useState('daily');
  const [template, setTemplate] = useState(null);
  const [form, setForm] = useState({ system_role: '', flag_criteria: '', analysis_priorities: '', additional_instructions: '' });
  const [originalForm, setOriginalForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  const hasChanges = originalForm && JSON.stringify(form) !== JSON.stringify(originalForm);

  const loadTemplate = useCallback(async (type) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from('ai_prompt_templates')
        .select('*')
        .eq('prompt_type', type)
        .eq('deployment_id', CLIENT_ID)
        .eq('is_active', true)
        .limit(1)
        .single();
      if (fetchErr && fetchErr.code !== 'PGRST116') throw fetchErr;
      if (data) {
        setTemplate(data);
        const formData = {
          system_role: data.system_role || '',
          flag_criteria: data.flag_criteria || '',
          analysis_priorities: data.analysis_priorities || '',
          additional_instructions: data.additional_instructions || '',
        };
        setForm(formData);
        setOriginalForm(formData);
      } else {
        setTemplate(null);
        const defaults = DEFAULTS[type];
        setForm({ ...defaults });
        setOriginalForm({ ...defaults });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplate(activeTab); }, [activeTab, loadTemplate]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (template) {
        const { error: updateErr } = await supabase
          .from('ai_prompt_templates')
          .update({
            system_role: form.system_role,
            flag_criteria: form.flag_criteria,
            analysis_priorities: form.analysis_priorities || null,
            additional_instructions: form.additional_instructions,
            updated_by: user?.id,
          })
          .eq('id', template.id);
        if (updateErr) throw updateErr;
      } else {
        const { error: insertErr } = await supabase
          .from('ai_prompt_templates')
          .insert({
            prompt_type: activeTab,
            name: 'Default',
            system_role: form.system_role,
            flag_criteria: form.flag_criteria,
            analysis_priorities: form.analysis_priorities || null,
            additional_instructions: form.additional_instructions,
            is_active: true,
            deployment_id: CLIENT_ID,
            created_by: user?.id,
            updated_by: user?.id,
          });
        if (insertErr) throw insertErr;
      }
      setSuccess('Template saved successfully!');
      setOriginalForm({ ...form });
      await loadTemplate(activeTab);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaults = DEFAULTS[activeTab];
    setForm({ ...defaults });
  };

  const handleDiscard = () => {
    if (originalForm) setForm({ ...originalForm });
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSuccess(null);
  };

  const textareaStyle = {
    width: '100%', minHeight: '120px', padding: '10px', fontFamily: 'monospace',
    fontSize: '13px', border: '1px solid #d0d0d0', borderRadius: '4px',
    resize: 'vertical', lineHeight: '1.5', boxSizing: 'border-box',
  };

  const tabStyle = (isActive) => ({
    padding: '10px 20px', border: 'none', borderBottom: isActive ? `3px solid ${BRANDING.primaryColor}` : '3px solid transparent',
    background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: isActive ? '600' : '400',
    color: isActive ? BRANDING.primaryColor : '#666',
  });

  return (
    <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: BRANDING.primaryColor, color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '16px' }}>AI Prompt Template Editor</h3>
        <button onClick={() => setShowGuide(!showGuide)} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
          {showGuide ? 'Hide Guide' : 'Show Guide'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0', background: '#fafafa' }}>
        <button onClick={() => setActiveTab('daily')} style={tabStyle(activeTab === 'daily')}>Daily Report</button>
        <button onClick={() => setActiveTab('weekly')} style={tabStyle(activeTab === 'weekly')}>Weekly Report</button>
        {hasChanges && <span style={{ padding: '10px 16px', fontSize: '12px', color: '#f57c00', fontWeight: '600', alignSelf: 'center' }}>* Unsaved changes</span>}
      </div>

      <div style={{ display: 'flex' }}>
        {/* Main Editor */}
        <div style={{ flex: 1, padding: '20px' }}>
          {loading ? (
            <p style={{ color: '#666', textAlign: 'center' }}>Loading template...</p>
          ) : (
            <>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>System Role</label>
                <p style={descStyle}>Defines the AI persona and analysis approach</p>
                <textarea style={textareaStyle} value={form.system_role} onChange={e => handleChange('system_role', e.target.value)} />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Flag Criteria</label>
                <p style={descStyle}>Severity classification rules for identifying issues</p>
                <textarea style={textareaStyle} value={form.flag_criteria} onChange={e => handleChange('flag_criteria', e.target.value)} />
              </div>

              {activeTab === 'weekly' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Analysis Priorities</label>
                  <p style={descStyle}>Strategic focus areas for weekly analysis</p>
                  <textarea style={textareaStyle} value={form.analysis_priorities} onChange={e => handleChange('analysis_priorities', e.target.value)} />
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Additional Instructions</label>
                <p style={descStyle}>Deployment-specific customizations and notes</p>
                <textarea style={{ ...textareaStyle, minHeight: '80px' }} value={form.additional_instructions} onChange={e => handleChange('additional_instructions', e.target.value)} />
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <button onClick={handleSave} disabled={saving} style={btnStyle(BRANDING.primaryColor)}>
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
                <button onClick={handleReset} style={btnStyle('#757575')}>Reset to Defaults</button>
                {hasChanges && <button onClick={handleDiscard} style={btnStyle('#d32f2f')}>Discard Changes</button>}
              </div>

              {error && <div style={{ marginTop: '12px', padding: '10px', background: '#fef2f2', color: '#d32f2f', borderRadius: '4px', fontSize: '12px' }}>{error}</div>}
              {success && <div style={{ marginTop: '12px', padding: '10px', background: '#f0fdf4', color: '#388e3c', borderRadius: '4px', fontSize: '12px' }}>{success}</div>}
            </>
          )}
        </div>

        {/* Side Panel Guide */}
        {showGuide && (
          <div style={{ width: '260px', borderLeft: '1px solid #e0e0e0', padding: '20px', background: '#fafafa', fontSize: '12px' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '13px', color: '#333' }}>Reference Guide</h4>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ color: BRANDING.primaryColor }}>Units ({LOCATIONS.length})</strong>
              <div style={{ marginTop: '4px' }}>
                {LOCATIONS.map(loc => (
                  <div key={loc.id} style={{ padding: '2px 0', color: '#555' }}>
                    {loc.name} <span style={{ color: '#999', fontSize: '10px' }}>({loc.type})</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ color: BRANDING.primaryColor }}>Medications</strong>
              <div style={{ marginTop: '4px' }}>
                {MEDICATIONS.map(med => (
                  <div key={med.id} style={{ padding: '2px 0', color: '#555' }}>
                    {med.displayName} <span style={{ color: '#999', fontSize: '10px' }}>Sch. {med.schedule}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong style={{ color: BRANDING.primaryColor }}>Severity Levels</strong>
              <div style={{ marginTop: '4px' }}>
                <div style={{ padding: '2px 0' }}><span style={{ color: '#d32f2f' }}>CRITICAL</span> — Immediate action</div>
                <div style={{ padding: '2px 0' }}><span style={{ color: '#f57c00' }}>WARNING</span> — Review needed</div>
                <div style={{ padding: '2px 0' }}><span style={{ color: '#1976d2' }}>INFO</span> — Notable event</div>
              </div>
            </div>

            <div>
              <strong style={{ color: BRANDING.primaryColor }}>Status Values</strong>
              <div style={{ marginTop: '4px' }}>
                <div style={{ padding: '2px 0' }}><span style={{ color: '#388e3c' }}>compliant</span></div>
                <div style={{ padding: '2px 0' }}><span style={{ color: '#f57c00' }}>needs_attention</span></div>
                <div style={{ padding: '2px 0' }}><span style={{ color: '#d32f2f' }}>non_compliant</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '4px' };
const descStyle = { margin: '0 0 6px', fontSize: '11px', color: '#888' };
function btnStyle(bg) {
  return {
    background: bg, color: '#fff', border: 'none', padding: '8px 16px',
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  };
}

export default PromptTemplateEditor;
